import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import type { BookingBrandColors } from '../../utils/booking-brand-colors';
import { buildBookingModalFormStyles } from '../../utils/booking-modal-form-styles';
import type { ShopGiftCardLine } from '../../services/shop';
import type { BookingCustomer } from '../../services/customer-auth.service';
import { formatMoneyFromCents } from '../../utils/format-money';

type ShopGiftCardPurchaseModalProps = {
  open: boolean;
  amountCents: number | null;
  minCents?: number;
  maxCents?: number;
  purchaser: BookingCustomer | null;
  brandColors: BookingBrandColors;
  onClose: () => void;
  onAddToCart: (line: ShopGiftCardLine) => void;
};

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

const ShopGiftCardPurchaseModal: React.FC<ShopGiftCardPurchaseModalProps> = ({
  open,
  amountCents,
  minCents,
  maxCents,
  purchaser,
  brandColors,
  onClose,
  onAddToCart,
}) => {
  const styles = useMemo(() => buildBookingModalFormStyles(brandColors), [brandColors]);
  const [recipientSameAsPurchaser, setRecipientSameAsPurchaser] = useState(true);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const switchSx = useMemo(
    () => ({
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: styles.accent,
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: styles.accent,
        opacity: 0.5,
      },
    }),
    [styles.accent],
  );

  useEffect(() => {
    if (!open) return;
    setRecipientSameAsPurchaser(true);
    setRecipientName('');
    setRecipientEmail('');
    setMessage('');
    setError(null);
  }, [open, amountCents, purchaser?.id]);

  const isGift = !recipientSameAsPurchaser;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (amountCents == null || amountCents <= 0) {
      setError('Enter a valid amount.');
      return;
    }
    if (minCents != null && amountCents < minCents) {
      setError(`Minimum amount is ${formatMoneyFromCents(minCents)}.`);
      return;
    }
    if (maxCents != null && amountCents > maxCents) {
      setError(`Maximum amount is ${formatMoneyFromCents(maxCents)}.`);
      return;
    }

    if (isGift) {
      if (!recipientEmail.trim()) {
        setError('Recipient email is required for gift delivery.');
        return;
      }
      if (!isValidEmail(recipientEmail)) {
        setError('Enter a valid recipient email.');
        return;
      }
    }

    setError(null);
    onAddToCart({
      type: 'gift_card',
      amountCents,
      ...(isGift
        ? {
            recipientEmail: recipientEmail.trim(),
            recipientName: recipientName.trim() || undefined,
            message: message.trim() || undefined,
          }
        : {}),
    });
    onClose();
  };

  const modalSubtitle =
    amountCents != null
      ? formatMoneyFromCents(amountCents)
      : purchaser
        ? 'Choose who receives this gift card.'
        : 'Sign in at checkout. Choose whether this card is for you or someone else.';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      scroll="paper"
      aria-labelledby="shop-gift-card-modal-title"
      BackdropProps={{ sx: styles.dialogBackdropSx }}
      PaperProps={{ sx: styles.dialogPaperSx }}
    >
      <DialogTitle component="div" sx={styles.dialogTitleShellSx}>
        <Box sx={{ px: styles.padX, pt: styles.padYTop, pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0, pr: 0.5 }}>
              <Typography component="h2" id="shop-gift-card-modal-title" sx={styles.modalTitleSx}>
                Add gift card
              </Typography>
              <Typography component="p" sx={styles.helperTextSx}>
                {modalSubtitle}
              </Typography>
            </Box>
            <IconButton
              aria-label="Close dialog"
              onClick={onClose}
              size="small"
              sx={styles.closeIconButtonSx}
            >
              <X size={18} strokeWidth={2.25} aria-hidden />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={styles.dialogContentSx}>
        {error ? (
          <Alert severity="error" sx={styles.errorAlertSx}>
            {error}
          </Alert>
        ) : null}

        <Stack component="form" spacing={styles.gapFields} onSubmit={handleSubmit}>
          {purchaser ? (
            <Typography component="p" sx={styles.helperTextSx}>
              Purchaser: {purchaser.firstName} {purchaser.lastName} · {purchaser.email}
            </Typography>
          ) : null}

          <FormControlLabel
            control={
              <Switch
                checked={purchaser ? recipientSameAsPurchaser : !recipientSameAsPurchaser}
                onChange={(e) =>
                  setRecipientSameAsPurchaser(
                    purchaser ? e.target.checked : !e.target.checked,
                  )
                }
                sx={switchSx}
              />
            }
            label={purchaser ? 'Same as purchaser' : 'This is a gift for someone else'}
            sx={styles.switchLabelSx}
          />

          {isGift ? (
            <>
              <Box>
                <Typography component="label" htmlFor="gift-recipient-name" sx={styles.fieldLabelSx}>
                  Recipient name (optional)
                </Typography>
                <TextField
                  id="gift-recipient-name"
                  hiddenLabel
                  placeholder="Recipient name"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  size="medium"
                  fullWidth
                  autoComplete="name"
                  sx={styles.inputSx}
                />
              </Box>

              <Box>
                <Typography component="label" htmlFor="gift-recipient-email" sx={styles.fieldLabelSx}>
                  Delivery email
                </Typography>
                <TextField
                  id="gift-recipient-email"
                  type="email"
                  hiddenLabel
                  placeholder="you@example.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  size="medium"
                  fullWidth
                  required
                  autoComplete="email"
                  sx={styles.inputSx}
                />
              </Box>

              <Box>
                <Typography component="label" htmlFor="gift-message" sx={styles.fieldLabelSx}>
                  Gift message (optional)
                </Typography>
                <TextField
                  id="gift-message"
                  hiddenLabel
                  placeholder="Add a personal note"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  size="medium"
                  fullWidth
                  multiline
                  minRows={3}
                  sx={styles.inputSx}
                />
              </Box>

              <Typography component="p" sx={styles.helperTextSx}>
                The gift card will be delivered digitally to the recipient email.
              </Typography>
            </>
          ) : (
            <Typography component="p" sx={styles.helperTextSx}>
              {purchaser
                ? 'The gift card will be added to your account after purchase.'
                : 'The gift card will be added to your account after you sign in and complete checkout.'}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disableElevation
            disabled={amountCents == null}
            sx={styles.primaryCtaSx}
          >
            Add to cart
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ShopGiftCardPurchaseModal;
